import {
  Box,
  Card,
  FormatNumber,
  HStack,
  Image,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";

import { useDailySpinOffchain } from "@/hooks/useDailySpinOffchain";
import { useDailySpinOnchain } from "@/hooks/useDailySpinOnchain";

type StatItemProps = {
  label: string;
  value: number;
  icon: React.ReactNode;
  isLoading?: boolean;
};

const StatItem = ({ label, value, icon, isLoading }: StatItemProps) => {
  return (
    <VStack gap={3} alignItems="start" minW="132px">
      <Text
        fontSize="sm"
        fontWeight="500"
        color="text.secondary"
        lineHeight="20px"
        letterSpacing="-0.09px"
      >
        {label}
      </Text>
      <HStack gap={2} alignItems="center">
        {icon}
        {isLoading ? (
          <Skeleton height="34px" width="60px" />
        ) : (
          <Text
            fontSize="28px"
            fontWeight="500"
            fontFamily="heading"
            lineHeight="34px"
            letterSpacing="-0.7px"
          >
            <FormatNumber value={value} />
          </Text>
        )}
      </HStack>
    </VStack>
  );
};

const PPIcon = () => (
  <Image src="/assets/daily-spin/stats-pp-icon.svg" boxSize="24px" alt="PP" />
);

const StreakIcon = () => (
  <Box
    bgColor="#352d03"
    borderRadius="full"
    boxSize="24px"
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <Image
      src="/assets/daily-spin/stats-lightning-icon.svg"
      boxSize="16px"
      alt="Streak"
    />
  </Box>
);

const TicketIcon = () => (
  <Box
    bgColor="#896efd"
    borderRadius="full"
    boxSize="24px"
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <Image
      src="/assets/daily-spin/stats-ticket-icon.svg"
      boxSize="15px"
      alt="Tickets"
    />
  </Box>
);

const PlumeIcon = () => (
  <Box
    bgColor="#ff3d00"
    borderRadius="full"
    boxSize="24px"
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <Image
      src="/assets/daily-spin/stats-plume-logo.svg"
      boxSize="15px"
      alt="Plume"
    />
  </Box>
);

export const DailySpinStats = () => {
  const {
    longestStreak,
    totalSpins,
    isLoading: isLoadingOffchain,
  } = useDailySpinOffchain();
  const { raffleTicketsWon, xpWon, plumeTokensWon, isLoadingDailySpinStats } =
    useDailySpinOnchain();

  const isLoading = isLoadingOffchain || isLoadingDailySpinStats;

  const ppEarned = xpWon ?? 0;
  const ticketsCollected = raffleTicketsWon ?? 0;
  const plumeEarned = plumeTokensWon ?? 0;

  return (
    <Card.Root
      bgColor="white"
      borderColor="#f0f0f0"
      borderWidth="1px"
      borderRadius="16px"
      w="full"
    >
      <Card.Body px={8} py={6}>
        <HStack
          gap={{ base: 6, md: 12, lg: 16 }}
          flexWrap="wrap"
          justifyContent={{ base: "flex-start", lg: "space-between" }}
        >
          <StatItem
            label="Total spins"
            value={totalSpins ?? 0}
            icon={<PPIcon />}
            isLoading={isLoading}
          />
          <StatItem
            label="Longest streak"
            value={longestStreak ?? 0}
            icon={<StreakIcon />}
            isLoading={isLoading}
          />
          <StatItem
            label="PP earned"
            value={ppEarned}
            icon={<PPIcon />}
            isLoading={isLoading}
          />
          <StatItem
            label="Tickets collected"
            value={ticketsCollected}
            icon={<TicketIcon />}
            isLoading={isLoading}
          />
          <StatItem
            label="Plume earned"
            value={plumeEarned}
            icon={<PlumeIcon />}
            isLoading={isLoading}
          />
        </HStack>
      </Card.Body>
    </Card.Root>
  );
};
